import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { TextField, FormLabel } from '@mui/material'
import Autocomplete from '@mui/lab/Autocomplete'
import { KeyboardArrowDown } from '@mui/icons-material'
import clsx from 'clsx'
import IconButton from '@mui/material/IconButton'
import { withStyles, makeStyles } from '~/themes/useStyles'
import { COLORS } from '~/themes/common'
import Icon from '~/components/common/Icon'
import OverLay from '../OverLay'

const style = (theme) => ({
  root: {
    '& > div > p': {
      marginLeft: 0,
    },
    '& .MuiInputBase-root .MuiAutocomplete-endAdornment': {
      right: 4,
    },
    '& .MuiInputBase-root.Mui-disabled': {
      background: COLORS.gray.disableGray2,
      pointerEvents: 'none',
    },
  },
  popperDisablePortal: {
    position: 'relative',
  },
  inputRoot: {
    height: 32,
  },
  option: {
    borderRadius: 5,
    '&:hover': {
      background: COLORS.palette.secondaryBlue3,
      color: COLORS.gray.darkGray,
    },
    fontSize: 14,
  },
  paper: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    boxShadow: '0px 2px 10px #D6DEF2',
    borderRadius: 5,
  },
})

const useTextFieldStyled = makeStyles({
  input: {
    '&.disabled': {
      color: COLORS.gray.disableGray,
    },
    padding: '0 4px !important',
    fontSize: 14,
  },
  cssFocused: {},
  notchedOutline: {},
  option: {
    minWidth: 120,
  },
  noOptions: {
    fontSize: '14px',
  },
})

const useFormLabelStyles = makeStyles((theme) => ({
  formLabel: {
    textAlign: 'left',
    fontSize: 14,
    letterSpacing: 0,
    color: COLORS.palette.black,
    marginBottom: theme.spacing(1),
    opacity: 1,
  },
}))

const useIconCloseStyles = makeStyles(() => ({
  clearRangePrice: {
    position: 'absolute',
    right: -2,
    bottom: -16,
    padding: 10,
  },
}))

const Dropdown = ({
  classes,
  options,
  label,
  placeholder,
  value,
  inputProps,
  readOnly,
  disabled,
  className,
  legend,
  onClear,
  ...props
}) => {
  const textFieldClasses = useTextFieldStyled()
  const formLabelClasses = useFormLabelStyles()
  const iconCloseClasses = useIconCloseStyles()
  const [overlay, setOverlay] = useState(false)
  return (
    <>
      {legend && (
        <FormLabel className={formLabelClasses.formLabel} component="legend">
          {legend}
        </FormLabel>
      )}
      <Autocomplete
        className={clsx(classes.root, className)}
        classes={{
          ...classes,
          popperDisablePortal: classes.popperDisablePortal,
        }}
        noOptionsText={<span className={classes.noOptions}>No Options</span>}
        options={options}
        value={
          options.find((e) => e.value === value) || { value: '', label: '' }
        }
        disableClearable
        openOnFocus
        isOptionEqualToValue={(option, val) => option.label === val.label}
        getOptionLabel={(option) => option.label || ''}
        popupIcon={
          onClear && value ? (
            <IconButton
              className={iconCloseClasses.clearRangePrice}
              size="small"
              onClick={onClear}
            >
              <Icon name="close" color={COLORS.palette.primaryBlue} size={10} />
            </IconButton>
          ) : (
            <KeyboardArrowDown color={disabled ? 'disabled' : 'primary'} />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              classes: {
                input: clsx(textFieldClasses.input, disabled && 'disabled'),
                focused: textFieldClasses.cssFocused,
                notchedOutline: clsx(
                  textFieldClasses.notchedOutline,
                  disabled && 'disabled',
                ),
              },
              inputProps: {
                readOnly,
                ...params.inputProps,
                autoComplete: 'new-password',
              },
            }}
            {...inputProps}
          />
        )}
        disabled={disabled}
        onOpen={() => {
          setOverlay(true)
          document.body.style.overflow = 'hidden'
        }}
        onClose={() => {
          setOverlay(false)
          document.body.style.overflow = ''
        }}
        {...props}
      />
      {overlay && <OverLay />}
    </>
  )
}

Dropdown.defaultProps = {
  label: '',
  options: [],
  placeholder: '',
  inputProps: {},
  value: '',
  readOnly: false,
  disabled: false,
  className: '',
  legend: '',
  onClear: null,
}

Dropdown.propTypes = {
  classes: PropTypes.shape().isRequired,
  className: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape()),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.shape()]),
  disabled: PropTypes.bool,
  legend: PropTypes.string,
  onClear: PropTypes.func,
}

export default withStyles(style)(Dropdown)
